import { IconButton } from "@/components/ui/buttons/IconButton";
import { FlashAlert } from "@/components/ui/feedback/FlashAlert";
import { StatusMessages } from "@/components/ui/feedback/StatusMessages";
import { Select } from "@/components/ui/forms/Select";
import type { SelectOption } from "@/components/ui/forms/Select/types";
import { FilteredList } from "@/components/ui/lists/FilteredList";
import { useOrganizations, usePageAccess } from "@/hooks/data";
import type { IOrganization, IPage } from "@/services";
import { Plus, X } from "lucide-react";
import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import layoutStyles from "@/components/admin/AdminSectionLayout.module.css";
import styles from "./PagesSection.module.css";
import type { PagesSectionProps } from "./types";
import SectionHeader from "@/components/layout/SectionHeader";

/**
 * PagesSection - Manage page access per organization
 * Part of the Superadmin page
 */
export const PagesSection: React.FC<PagesSectionProps> = ({
  className,
  "data-testid": testId = "pages-section",
}) => {
  const { activeOrganizations, isLoading: orgsLoading } = useOrganizations();
  const {
    pages,
    isLoading: pagesLoading,
    error,
    hasAccess,
    addAccess,
    removeAccess,
    isUpdating,
  } = usePageAccess();

  // Selected organization
  const [selectedOrg, setSelectedOrg] = useState<SelectOption<IOrganization>[]>(
    [],
  );

  // Flash alerts
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isLoading = orgsLoading || pagesLoading;
  const selectedOrgId = selectedOrg[0]?.data?.id ?? null;

  // Convert organizations to select options
  const orgOptions: SelectOption<IOrganization>[] = useMemo(() => {
    return activeOrganizations.map((org) => ({
      id: org.id,
      label: `${org.tenant_name} (${org.tenant_id})`,
      data: org,
    }));
  }, [activeOrganizations]);

  // Render org option with tenant ID
  const renderOrgOption = useCallback(
    (option: SelectOption<IOrganization>) => (
      <div
        style={{ display: "flex", flexDirection: "column", gap: "0.125rem" }}
      >
        <span style={{ fontWeight: 500 }}>
          {option.data?.tenant_name ?? option.label}
        </span>
        {option.data?.tenant_id && (
          <span
            style={{
              fontSize: "0.75rem",
              color: "var(--color-text-secondary, #666)",
              fontFamily: "monospace",
            }}
          >
            {option.data.tenant_id}
          </span>
        )}
      </div>
    ),
    [],
  );

  // Handle toggle access
  const handleToggleAccess = useCallback(
    async (page: IPage) => {
      if (!selectedOrgId) return;

      const orgHasAccess = hasAccess(selectedOrgId, page.id);
      let success: boolean;

      if (orgHasAccess) {
        success = await removeAccess(selectedOrgId, page.id);
        if (success) {
          setSuccessMessage(`Tilgang til "${page.name}" er fjernet`);
        } else {
          setErrorMessage(`Kunne ikke fjerne tilgang til "${page.name}"`);
        }
      } else {
        success = await addAccess(selectedOrgId, page.id);
        if (success) {
          setSuccessMessage(`Tilgang til "${page.name}" er lagt til`);
        } else {
          setErrorMessage(`Kunne ikke legge til tilgang til "${page.name}"`);
        }
      }
    },
    [selectedOrgId, hasAccess, addAccess, removeAccess],
  );

  // Render page item
  const renderPageItem = useCallback(
    (page: IPage) => {
      const orgHasAccess = selectedOrgId
        ? hasAccess(selectedOrgId, page.id)
        : false;

      return (
        <div
          key={page.id}
          className={styles.pageItem}
          data-testid={`${testId}-page-${page.id}`}
        >
          <div className={styles.pageInfo}>
            <div className={styles.pageName}>{page.name}</div>
            <div className={styles.pageRoute}>{page.route}</div>
          </div>
          <IconButton
            icon={orgHasAccess ? <X size={16} /> : <Plus size={16} />}
            onClick={() => handleToggleAccess(page)}
            disabled={isUpdating || !selectedOrgId}
            aria-label={
              orgHasAccess
                ? `Fjern tilgang til ${page.name}`
                : `Legg til tilgang til ${page.name}`
            }
            size="sm"
            shape="square"
            className={classNames(
              styles.toggleButton,
              orgHasAccess ? styles.removeButton : styles.addButton,
            )}
            data-testid={`${testId}-toggle-${page.id}`}
          />
        </div>
      );
    },
    [selectedOrgId, hasAccess, handleToggleAccess, isUpdating, testId],
  );

  return (
    <div
      className={classNames(layoutStyles.section, styles.section, className)}
      data-testid={testId}
    >
      {/* Explanation text - visible only on desktop */}
      <p className={layoutStyles.explanationText}>
        Kontroller hvilke sider i portalen hver organisasjon kan se. Sider som
        ikke er aktivert vil ikke vises i navigasjonen for den organisasjonen.
      </p>

      {/* Flash alerts */}
      {successMessage && (
        <FlashAlert
          alert={{ message: successMessage, variant: "success" }}
          onDismiss={() => setSuccessMessage(null)}
          data-testid={`${testId}-success-alert`}
        />
      )}
      {errorMessage && (
        <FlashAlert
          alert={{ message: errorMessage, variant: "error" }}
          onDismiss={() => setErrorMessage(null)}
          data-testid={`${testId}-error-alert`}
        />
      )}

      <div className={layoutStyles.card}>
        <SectionHeader as="h2" size="md">
          Sider per organisasjon
        </SectionHeader>

        {/* Organization selector */}
        <div className={styles.selectorWrapper}>
          <div className={styles.formField}>
            <label htmlFor="pages-org-select" className={styles.fieldLabel}>
              Velg organisasjon
            </label>
            <Select<IOrganization>
              id="pages-org-select"
              appearance="clean"
              placeholder="Søk etter organisasjon..."
              size="md"
              options={orgOptions}
              value={selectedOrg}
              onChange={setSelectedOrg}
              searchable
              disabled={isLoading}
              renderOption={renderOrgOption}
              data-testid={`${testId}-org-select`}
            />
          </div>
        </div>

        <StatusMessages
          error={error}
          isLoading={isLoading}
          errorLabel="Kunne ikke laste sider"
          loadingLabel="Laster sider..."
          testId={testId}
        />

        {/* Pages list */}
        {!isLoading && !error && (
          <>
            {selectedOrgId ? (
              <FilteredList
                items={pages}
                filterItem={(page, normalized) => {
                  const searchFields = [page.name, page.route];
                  return searchFields.some((value) =>
                    value.toLowerCase().includes(normalized),
                  );
                }}
                renderItem={renderPageItem}
                placeholder="Søk etter side"
                debounce={150}
                className={styles.listWrapper}
                emptyState={
                  <div className={styles.emptyState}>Ingen sider funnet</div>
                }
                data-testid={`${testId}-list`}
              />
            ) : (
              <div className={styles.noOrgSelected}>
                Velg en organisasjon for å administrere sidetilgang
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

PagesSection.displayName = "PagesSection";

export default PagesSection;
